import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  Divider,
} from '@mui/material';

const departments = [
  { key: 'owner', label: "I'm a business owner" },
  { key: 'support', label: 'Customer Support' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'webdev', label: 'Website development' },
  { key: 'it', label: 'IT/Technical' },
  { key: 'ops', label: 'Operations' },
  { key: 'sales', label: 'Sales' },
  { key: 'other', label: 'Other' },
];

const sources = [
  'Online course',
  'ChatGPT',
  'Blog',
  'Youtube',
  'Someone told me about it',
  'Instagram',
  'Podcast',
  'Google',
  'Facebook',
  'Other',
];

export default function OnboardingProfile() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [dept, setDept] = useState('');
  const [source, setSource] = useState('');
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      // Persist lightweight answers locally; DB wiring can be added later
      try {
        localStorage.setItem('onboarding_name', name);
        localStorage.setItem('onboarding_department', dept);
        localStorage.setItem('onboarding_source', source);
      } catch (_) {}

      // Persist to DB (idempotent upsert)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (userId) {
          await supabase
            .from('user_onboarding')
            .upsert({
              user_id: userId,
              name: name || null,
              department: dept || null,
              source: source || null,
            }, { onConflict: 'user_id' });
        }
      } catch (e) {
        // Non-blocking
        console.warn('[OnboardingProfile] Failed to persist onboarding answers:', e);
      }
    } finally {
      setSaving(false);
      navigate('/onboarding/profile-2');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={3}>
              <div>
                <Typography variant="h6" gutterBottom>What’s your name?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Your answers will help us personalize your experience
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <Typography variant="h6" gutterBottom>Which department do you work in?</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {departments.map((d) => (
                    <Chip
                      key={d.key}
                      label={d.label}
                      color={dept === d.key ? 'primary' : 'default'}
                      onClick={() => setDept(d.key)}
                      variant={dept === d.key ? 'filled' : 'outlined'}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </div>

              <div>
                <Typography variant="h6" gutterBottom>Where did you learn about us?</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {sources.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      color={source === s ? 'primary' : 'default'}
                      onClick={() => setSource(s)}
                      variant={source === s ? 'filled' : 'outlined'}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </div>

              <Divider />

              <Stack direction="row" spacing={2}>
                <Button onClick={() => navigate('/onboarding/usage-choice')}>
                  Skip for now
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={handleContinue} disabled={saving}>
                  Continue
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: { xs: 320, md: 560 }, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Preview</Typography>
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', borderRadius: 1 }}>
              {/* Placeholder chat preview box */}
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1, mb: 1, width: '85%' }}>
                  <Typography variant="body2" color="text.secondary">Welcome, {name || 'there'}! 👋</Typography>
                </Box>
                <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1, p: 1, ml: '15%', mb: 1, width: '80%' }}>
                  <Typography variant="body2">I’m here to help with {dept ? departments.find(d=>d.key===dept)?.label.toLowerCase() : 'anything you need'}.</Typography>
                </Box>
                <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1, width: '70%' }}>
                  <Typography variant="body2" color="text.secondary">Let’s get started.</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}





